{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "ESP-IDF component manifest (idf_component.yml)",
  "description": "Describes an ESP-IDF component and its dependencies. The manifest sits in the component's root directory.",
  "type": "object",
  "properties": {
    "dependencies": {
      "description": "Components this component depends on, by name. A registry component is `namespace/name`, where the `espressif/` namespace can be left out. The value is a version range, or a mapping with the dependency's source and conditions.",
      "type": "object",
      "properties": {
        "idf": {
          "description": "The ESP-IDF versions the component works with, as a version range such as `>=5.0`.",
          "oneOf": [
            { "type": "string", "description": "A version range, such as `>=5.0`." },
            {
              "type": "object",
              "properties": {
                "version": { "type": "string", "description": "A version range, such as `>=5.0`." }
              }
            }
          ]
        }
      },
      "additionalProperties": {
        "oneOf": [
          { "type": "string", "description": "A version or version range of a registry component, such as `^1.0.0`." },
          { "$ref": "#/$defs/DependencyItem" }
        ]
      },
      "default": {}
    },
    "targets": {
      "description": "The chips the component supports. Leave it out if the component supports every target.",
      "type": "array",
      "items": { "type": "string" },
      "uniqueItems": true,
      "default": []
    },
    "version": {
      "description": "The component's version, such as `1.0.0`. Required to upload the component to the ESP Component Registry, unless given with a Git tag or on the upload command line.",
      "type": "string",
      "minLength": 1
    },
    "description": {
      "description": "A short description of the component. The registry warns on upload when it is missing.",
      "type": "string"
    },
    "maintainers": {
      "description": "The component's maintainers, as `Name <email>`.",
      "type": "array",
      "items": { "type": "string" },
      "uniqueItems": true
    },
    "license": {
      "description": "The component's license, as an SPDX identifier such as `MIT` or `Apache-2.0`. Without it, a LICENSE or LICENSE.txt file in the component gives the license.",
      "type": "string"
    },
    "tags": {
      "description": "Keywords for what the component does, 3 to 32 letters, digits, `_` or `-` each.",
      "type": "array",
      "items": { "type": "string", "pattern": "^[A-Za-z0-9\\_\\-]{3,32}$" },
      "uniqueItems": true,
      "default": []
    },
    "files": {
      "$ref": "#/$defs/FilesField"
    },
    "examples": {
      "description": "Directories with example projects, for examples outside the component's `examples` directory. The registry finds those under `examples` by itself.",
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "path": { "type": "string", "description": "The example's directory, relative to the component." }
        },
        "additionalProperties": true
      }
    },
    "url": {
      "description": "The component's website. The registry warns on upload when it is missing.",
      "type": "string"
    },
    "repository": {
      "description": "The Git remote URL of the component's repository.",
      "type": "string"
    },
    "repository_info": {
      "$ref": "#/$defs/RepositoryInfoField"
    },
    "documentation": {
      "description": "The URL of the component's documentation.",
      "type": "string"
    },
    "issues": {
      "description": "The URL of the component's issue tracker.",
      "type": "string"
    },
    "discussion": {
      "description": "The URL of the component's discussion forum or chat.",
      "type": "string"
    },
    "name": {
      "description": "The component's name. The registry takes it from the upload command, so it is rarely set here.",
      "type": "string"
    }
  },
  "$defs": {
    "DependencyItem": {
      "type": "object",
      "properties": {
        "version": {
          "description": "For a registry component, a version or version range such as `>=2.0`. For a Git dependency, a branch, tag or commit; the default branch when left out.",
          "type": "string"
        },
        "path": {
          "description": "A local dependency's directory, relative to this manifest or absolute; or, with `git`, the component's directory in the repository. Supports environment variables.",
          "type": "string"
        },
        "git": {
          "description": "The URL or local path of the Git repository the dependency comes from. Supports environment variables.",
          "type": "string"
        },
        "override_path": {
          "description": "A local directory to use instead of downloading the component from the registry, for example in an example project inside the component. Supports environment variables.",
          "type": "string"
        },
        "require": {
          "description": "How the dependency is required: `private` (the default) as PRIV_REQUIRES, `public` as REQUIRES, or `no` to download it without requiring it.",
          "oneOf": [
            { "enum": ["public", "private", "no"], "type": "string" },
            { "const": false, "type": "boolean" }
          ]
        },
        "rules": {
          "description": "Conditions for the dependency: it is included when all of the `if` clauses are true.",
          "type": "array",
          "items": { "$ref": "#/$defs/OptionalDependency" }
        },
        "matches": {
          "description": "Conditions for the dependency: it is included when any of the `if` clauses is true.",
          "type": "array",
          "items": { "$ref": "#/$defs/OptionalDependency" }
        },
        "pre_release": {
          "description": "Whether pre-release versions of the dependency may be used. They are ignored by default.",
          "type": "boolean"
        },
        "registry_url": {
          "description": "The ESP Component Registry to resolve the dependency from; `https://components.espressif.com` by default.",
          "type": "string"
        },
        "public": {
          "description": "Deprecated: use `require: public`.",
          "type": "boolean",
          "deprecated": true
        },
        "service_url": {
          "description": "Deprecated: use `registry_url`.",
          "type": "string",
          "deprecated": true
        }
      }
    },
    "OptionalDependency": {
      "type": "object",
      "properties": {
        "if": {
          "description": "A condition such as `target in [esp32, esp32c3]`, `idf_version >=5.0`, `$ENV_VAR == value` or `$CONFIG{OPTION} == True`. Combine conditions with `&&`, `||` and parentheses.",
          "type": "string"
        },
        "version": {
          "description": "The dependency's version or version range when this condition is true, instead of the dependency's `version`.",
          "type": "string"
        }
      },
      "required": ["if"]
    },
    "FilesField": {
      "description": "Which files go into the archive uploaded to the registry, or are used from a Git dependency.",
      "type": "object",
      "properties": {
        "use_gitignore": {
          "description": "Exclude the files `.gitignore` excludes, instead of the default exclusion list.",
          "type": "boolean",
          "default": false
        },
        "exclude": {
          "description": "Glob patterns of files to exclude, such as `**/*.py`.",
          "type": "array",
          "items": { "type": "string" },
          "uniqueItems": true,
          "default": []
        },
        "include": {
          "description": "Glob patterns of files to include even when excluded otherwise.",
          "type": "array",
          "items": { "type": "string" },
          "uniqueItems": true,
          "default": []
        }
      }
    },
    "RepositoryInfoField": {
      "description": "Where in `repository` the component is. Requires `repository`.",
      "type": "object",
      "properties": {
        "path": {
          "description": "The component's directory in the repository, when it is not at the root.",
          "type": "string"
        },
        "commit_sha": {
          "description": "The full commit SHA the component was uploaded from.",
          "type": "string",
          "pattern": "[0-9a-f]{40}"
        }
      }
    }
  }
}
